import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, Eye, EyeOff } from 'lucide-react';
import api from '../../../api/client';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirm } from '../../../contexts/ConfirmContext';

interface EmailSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmailSettingsModal: React.FC<EmailSettingsModalProps> = ({ isOpen, onClose }) => {
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const [provider, setProvider] = useState<'yandex' | 'mail' | 'custom'>('yandex');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(465);
  const [smtpSecure, setSmtpSecure] = useState(true);
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) loadSettings();
  }, [isOpen]);

  const loadSettings = async () => {
    try {
      const res = await api.get('/user/email-settings');
      const data = res.data;
      if (data.provider) setProvider(data.provider);
      if (data.email) setEmail(data.email);
      if (data.smtpHost) setSmtpHost(data.smtpHost);
      if (data.smtpPort) setSmtpPort(data.smtpPort);
      if (data.smtpSecure !== undefined) setSmtpSecure(data.smtpSecure);
      setHasPassword(data.hasPassword);
    } catch (err) {
      console.error('Ошибка загрузки настроек', err);
    }
  };

  const handleSave = async () => {
    if (!email || !password) {
      setError('Заполните email и пароль');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/user/email-settings', {
        provider,
        email,
        password,
        smtpHost: provider === 'custom' ? smtpHost : undefined,
        smtpPort: provider === 'custom' ? smtpPort : undefined,
        smtpSecure: provider === 'custom' ? smtpSecure : undefined,
      });
      showToast('Настройки почты успешно сохранены', 'success');
      onClose();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Ошибка сохранения настроек';
      setError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    confirmDialog.showConfirm({
      title: 'Удаление настроек почты',
      message: 'Вы уверены, что хотите удалить настройки почты? Это отключит отправку писем.',
      confirmText: 'Удалить',
      onConfirm: async () => {
        try {
          await api.delete('/user/email-settings');
          showToast('Настройки почты удалены', 'success');
          onClose();
        } catch (err) {
          showToast('Ошибка удаления настроек', 'error');
        }
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Настройки почты</h3>
          <button onClick={onClose} style={styles.closeButton}><X size={20} /></button>
        </div>
        <div style={styles.body}>
          <div style={styles.field}>
            <label style={styles.label}>Провайдер</label>
            <select value={provider} onChange={e => setProvider(e.target.value as any)} style={styles.select}>
              <option value="yandex">Яндекс Почта</option>
              <option value="mail">Mail.ru</option>
              <option value="custom">Свой SMTP</option>
            </select>
          </div>

          {provider === 'yandex' && (
            <div style={styles.infoBox}>
              <p style={styles.infoText}>
                🔐 Для Яндекс.Почты требуется <strong>пароль приложения</strong>. 
                Получите его в настройках безопасности Яндекса:{' '}
                <a href="https://id.yandex.ru/security/app-passwords" target="_blank" rel="noopener noreferrer" style={styles.infoLink}>
                  https://id.yandex.ru/security/app-passwords
                </a>
              </p>
            </div>
          )}

          {provider === 'mail' && (
            <div style={styles.infoBox}>
              <p style={styles.infoText}>
                🔐 Для Mail.ru требуется <strong>пароль приложения</strong>. 
                Создайте его в настройках безопасности вашего почтового ящика Mail.ru.
              </p>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Email *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={styles.input} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Пароль * {hasPassword && '(будет заменён)'}</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={styles.passwordInput}
                placeholder="Введите пароль приложения"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {provider === 'custom' && (
            <>
              <div style={styles.field}>
                <label style={styles.label}>SMTP Хост</label>
                <input type="text" value={smtpHost} onChange={e => setSmtpHost(e.target.value)} style={styles.input} placeholder="smtp.example.com" />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>SMTP Порт</label>
                <input type="number" value={smtpPort} onChange={e => setSmtpPort(parseInt(e.target.value))} style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>
                  <input type="checkbox" checked={smtpSecure} onChange={e => setSmtpSecure(e.target.checked)} /> SSL/TLS
                </label>
              </div>
            </>
          )}
          {error && <div style={styles.error}>{error}</div>}
        </div>
        <div style={styles.footer}>
          <button onClick={handleSave} disabled={loading} style={styles.saveButton}>
            <Save size={16} style={{ marginRight: '4px' }} /> Сохранить
          </button>
          {hasPassword && (
            <button onClick={handleDelete} style={styles.deleteButton}>
              <Trash2 size={16} style={{ marginRight: '4px' }} /> Удалить
            </button>
          )}
          <button onClick={onClose} style={styles.cancelButton}>Отмена</button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, backdropFilter: 'blur(4px)'
  },
  modal: {
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '24px',
    width: '480px',
    maxWidth: '90%',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.3)'
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { fontSize: '20px', fontWeight: 600, margin: 0, color: '#1a1a2e' },
  closeButton: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' },
  body: { marginBottom: '24px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#4a4a68' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.7)' },
  select: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)', background: 'rgba(255,255,255,0.7)' },
  passwordWrapper: { position: 'relative', width: '100%' },
  passwordInput: {
    width: '100%',
    padding: '10px 12px',
    paddingRight: '40px',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.7)'
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#666',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  error: { color: '#ff4d4f', fontSize: '13px', marginTop: '8px', textAlign: 'center' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  saveButton: { background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', color: 'white', border: 'none', borderRadius: '30px', padding: '8px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  deleteButton: { background: '#ff4d4f', color: 'white', border: 'none', borderRadius: '30px', padding: '8px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  cancelButton: { background: 'transparent', border: '1px solid #2575fc', color: '#2575fc', borderRadius: '30px', padding: '8px 20px', cursor: 'pointer' },
  infoBox: {
    background: 'rgba(37,117,252,0.1)',
    borderRadius: '12px',
    padding: '12px',
    marginBottom: '16px',
    border: '1px solid rgba(37,117,252,0.3)'
  },
  infoText: {
    fontSize: '13px',
    color: '#2575fc',
    margin: 0,
    lineHeight: 1.4
  },
  infoLink: {
    color: '#2575fc',
    textDecoration: 'underline',
    wordBreak: 'break-all'
  }
};

export default EmailSettingsModal;